import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DriverForm } from "./DriverModal";
import { Company, Driver, Vehicle } from "../../models";
import { IDriver } from "../../types";
import { useCache } from "../../context/CacheContext";

/** Own the draft independently from background query refreshes. */
export function DriverProfileForm({
  driver,
  companies,
  vehicles,
  drivers,
  canManage,
  onSaved,
  onDirty,
}: {
  driver: Driver;
  companies: Company[];
  vehicles: Vehicle[];
  drivers: Driver[];
  canManage: boolean;
  onSaved: () => Promise<unknown>;
  onDirty: (dirty: boolean) => void;
}) {
  const { t } = useTranslation("driver");
  const { updateCacheKey } = useCache();
  const [source, setSource] = useState(driver);
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const reset = (value: Driver) => {
    setSource(value);
    setRevision((value) => value + 1);
    onDirty(false);
  };
  const save = async (data: IDriver) => {
    if (!canManage || busy.current) return false;
    busy.current = true;
    setSaving(true);
    setSaved(false);
    try {
      const updated = await Driver.update(source, {
        ...data,
        email: data.email.trim().toLowerCase(),
      });
      updateCacheKey();
      if (mounted.current) {
        reset(updated);
        setSaved(true);
      }
      // A refresh failure must not present a successfully saved write as failed.
      void onSaved().catch(() => undefined);
      return true;
    } catch {
      return false;
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-3xl">
        <DriverForm
          key={revision}
          inline
          isShowing
          data={source}
          companies={companies}
          vehicles={vehicles}
          existingDrivers={drivers}
          readOnly={!canManage}
          isSubmitting={saving}
          onDirty={() => {
            onDirty(true);
            setSaved(false);
          }}
          onCancel={() => {
            reset(driver);
            setSaved(false);
          }}
          onConfirm={save}
        />
        {saved && (
          <p role="status" className="text-sm text-secondary">
            {t("profile.saved")}
          </p>
        )}
      </div>
    </div>
  );
}
